import { useEffect, useState } from 'react'
import { Icon } from './Icon'
import {
  api,
  isBlocking,
  type Member,
  type NavItem,
  type State,
  type Problem,
  type StagedChange,
} from '../api'
import { useDialog } from '../useDialog'
import { isSiteFile, type SiteFile } from '@staleless/schema'
import { draftLabel } from '../drafts'

/**
 * The checkpoint between autosaved work and review.
 *
 * The product's own model — stage, commit, push, publish — is git's, and it
 * was the writer's to learn: four buttons in a drawer, in an order nothing on
 * screen explained, with the irreversible one third. What a person actually
 * decides is smaller than that: these changes, to these pages, now — or ask
 * somebody to look first.
 *
 * This sheet says what changed in reader-facing language, records why, and
 * makes one explicit commit. Merging and publishing remain separate decisions.
 */

/**
 * Where a page SITS, as a reader would say it: "Get started", or
 * "Guides › Workflow".
 *
 * A list of six titles says what changed and not what part of the
 * documentation is moving under readers' feet — and the answer is already on
 * screen behind the sheet, in the tree. Read from the navigation rather than
 * from the file path, because the path is storage and the navigation is what
 * readers actually see.
 */
function placeOf(path: string, nav: State['nav']): string | null {
  if (!path.startsWith('pages/')) return null
  const id = path.replace(/^pages\//, '').replace(/\.md$/, '')
  const find = (items: NavItem[], trail: string[]): string | null => {
    for (const item of items) {
      if (item.page === id) return trail.join(' › ') || null
      if (item.items) {
        const deeper = find(item.items, item.label ? [...trail, item.label] : trail)
        if (deeper !== null) return deeper
      }
    }
    return null
  }
  for (const section of nav.sections) {
    const found = find(section.items, [section.label])
    if (found !== null) return found
  }
  return null
}

/**
 * What a publish calls each site file.
 *
 * A `Record` over the type rather than a chain, so a sixth site file will not
 * compile until it has a name a person can read. The chain this replaces WAS
 * complete — all six — which is exactly why it was worth converting: nothing
 * held it complete, and the same list is wrong in four other places. Its own
 * comment records the last time one fell off: a publish listed `search.yaml`,
 * a file path at a person, which is the one thing this sheet exists to stop.
 */
const SITE_FILE_NAMES: Record<SiteFile, string> = {
  'nav.yaml': 'The navigation',
  'variables.yaml': 'The variables',
  'search.yaml': 'What a reader’s search finds',
  'redirects.yaml': 'The redirects',
  'glossary.yaml': 'The words this documentation uses',
  'style.yaml': 'The style guide',
}

/** A staged path as the page it is. `pages/guides/install.md` → its title. */
function nameFor(path: string, titles: Map<string, string>): string {
  if (isSiteFile(path)) return SITE_FILE_NAMES[path as SiteFile]
  /*
   * A shared block, which stages exactly like a page and is named nowhere near
   * like one. The titles above come from `/api/state`, which leaves blocks out
   * of `pages` on purpose — so the sheet fell through to the path and said
   * `snippets/support`, which is the one thing it exists to stop. A block has
   * no title to fall back on: its NAME is what a page references it by, and
   * what the trash and the variables screen both call it.
   */
  const block = /^snippets\/(.+)\.md$/.exec(path)
  if (block) return `The shared block “${block[1]}”`
  const id = path.replace(/^pages\//, '').replace(/\.md$/, '')
  return titles.get(id) ?? id
}

/**
 * Whether this change can be shown as a before and after.
 *
 * `/api/compare` renders two documents, so it answers "Only pages can be
 * compared as rendered documents" for anything that is not Markdown — and the
 * name of every change was a button that asked it. The navigation is staged by
 * nearly every publish there is, so the commonest row in this sheet was a
 * control whose only possible outcome was an error message.
 */
function canBeRead(path: string): boolean {
  return path.endsWith('.md')
}

const DID: Record<StagedChange['op'], string> = {
  write: 'edited',
  move: 'moved',
  delete: 'deleted',
}

export function PublishSheet({
  open,
  branch,
  staged,
  problems,
  titles,
  nav,
  people,
  youId,
  busy,
  mayCommit,
  aimedAt,
  onClose,
  onCommit,
  onSendForReview,
  onOpenPage,
}: {
  open: boolean
  branch: string | null
  staged: StagedChange[]
  problems: Problem[]
  /** Page id to title, so a change can be named rather than pathed. */
  titles: Map<string, string>
  /** The navigation, so each change can say which part of the docs it is in. */
  nav: State['nav']
  people: Member[]
  youId: string | null
  busy: boolean
  /** Whether this person may write and therefore commit this draft. */
  mayCommit: boolean
  /** The frozen version this lands in, when it is not the live site. */
  aimedAt: string | null
  onClose: () => void
  onCommit: (note: string) => void
  onSendForReview: (note: string, reviewer: string | null) => void
  onOpenPage: (path: string) => void
}) {
  const [note, setNote] = useState('')
  const [reviewer, setReviewer] = useState<string>('')
  /** Which change is being read, and the two sides of it. */
  const [reading, setReading] = useState<string | null>(null)
  const [compared, setCompared] = useState<{ before: string | null; after: string | null } | null>(
    null,
  )
  const [compareError, setCompareError] = useState<string | null>(null)
  const panel = useDialog<HTMLDivElement>(open, onClose)

  useEffect(() => {
    if (!open) {
      setReading(null)
      setCompared(null)
      setCompareError(null)
      setNote('')
    }
  }, [open])

  useEffect(() => {
    if (!open || !branch || !reading) return
    let gone = false
    setCompared(null)
    setCompareError(null)
    void api
      .compare(branch, reading)
      .then((r) => {
        if (!gone) setCompared(r)
      })
      .catch((e: unknown) => {
        if (!gone) setCompareError(e instanceof Error ? e.message : String(e))
      })
    return () => {
      gone = true
    }
  }, [open, branch, reading])

  if (!open) return null

  const blocking = problems.filter(isBlocking)
  const others = people.filter(
    (person) =>
      person.userId !== youId &&
      (person.role === 'owner' || person.role === 'admin' || person.canReview || person.canPublish),
  )
  const ready = staged.length > 0 && blocking.length === 0

  return (
    <div className="scrim" onMouseDown={onClose}>
      <div
        className="sheet sheet--publish"
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label="Commit changes"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="sheet__head">
          <h2 className="sheet__title">
            <Icon name="commit" size={17} />
            Commit this draft
          </h2>
          <button className="btn btn--ghost btn--icon" onClick={onClose}>
            <Icon name="close" size={15} label="Close" />
          </button>
        </header>

        <div className="sheet__body">
          {staged.length === 0 ? (
            <p className="nothing">
              Nothing is waiting to be committed. Edit a page and save it first.
            </p>
          ) : (
            <>
              <div className="label">
                {staged.length} change{staged.length === 1 ? '' : 's'} to commit
              </div>
              {/*
                WHICH DRAFT is going out, said before the button rather than
                assumed. The heading says where the changes LAND — the live
                site, or a version — and never where they came FROM, so a
                writer who could not see a draft in the editor could read this
                sheet as publishing from nowhere. The same words the top bar's
                chip and the review's picker use.
              */}
              {branch && (
                <p className="sheet__from">
                  from <strong>{draftLabel(branch)}</strong>, the draft you are working on
                </p>
              )}
              <ul className="publist">
                {staged.map((change) => (
                  <li key={change.path} className="publist__row">
                    <span className={`dot dot--${change.op}`} />
                    {canBeRead(change.path) ? (
                      <button
                        className="publist__name truncate"
                        onClick={() => setReading(reading === change.path ? null : change.path)}
                        aria-expanded={reading === change.path}
                      >
                        {nameFor(change.path, titles)}
                      </button>
                    ) : (
                      <span className="publist__name truncate">{nameFor(change.path, titles)}</span>
                    )}
                    {placeOf(change.path, nav) && (
                      <span className="publist__where truncate">{placeOf(change.path, nav)}</span>
                    )}
                    <span className="publist__did">{DID[change.op]}</span>
                    {change.path.startsWith('pages/') && (
                      <button
                        className="btn btn--ghost btn--sm"
                        onClick={() => {
                          onClose()
                          onOpenPage(change.path)
                        }}
                      >
                        Open
                      </button>
                    )}
                  </li>
                ))}
              </ul>

              {reading && (
                <section className="publish__read">
                  <div className="label">{nameFor(reading, titles)}: before and after</div>
                  {compareError ? (
                    <p className="screen__error">
                      <Icon name="alert" size={13} /> The two sides could not be compared:{' '}
                      {compareError}
                    </p>
                  ) : compared === null ? (
                    <div className="screen__loading" aria-busy="true">
                      <p className="sr-only">Comparing…</p>
                      <span className="skeleton" />
                      <span className="skeleton" />
                    </div>
                  ) : (
                    <div className="compare">
                      <div className="compare__pane">
                        <div className="compare__title">Live site</div>
                        {compared.before != null ? (
                          <div
                            className="preview compare__doc"
                            dangerouslySetInnerHTML={{ __html: compared.before }}
                          />
                        ) : (
                          <p className="drawer__hint">New page — nothing live yet.</p>
                        )}
                      </div>
                      <div className="compare__pane">
                        <div className="compare__title">This draft</div>
                        {compared.after != null ? (
                          <div
                            className="preview compare__doc"
                            dangerouslySetInnerHTML={{ __html: compared.after }}
                          />
                        ) : (
                          <p className="drawer__hint">Deleted — readers will not find it.</p>
                        )}
                      </div>
                    </div>
                  )}
                </section>
              )}

              {blocking.length > 0 && (
                <section className="publish__blocked">
                  <div className="label">Publishing is held up</div>
                  <ul className="problemlist">
                    {blocking.map((problem, i) => (
                      <li key={i} className="problemlist__row">
                        <Icon name="alert" size={13} />
                        <span>
                          <span className="mono">{problem.file}</span> — {problem.message}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <p className="drawer__hint">
                    The server refuses these too, so they are the whole of what is in the way.
                  </p>
                </section>
              )}

              <label className="field">
                <span className="label">What changed, and why</span>
                <input
                  className="input"
                  value={note}
                  placeholder="Rewrote the install steps for Node 24"
                  disabled={busy}
                  onChange={(e) => setNote(e.target.value)}
                />
                <span className="field__note">This becomes the permanent name of the commit.</span>
              </label>
            </>
          )}
        </div>

        <footer className="sheet__foot">
          <div className="publish__actions">
            {mayCommit && (
              <button
                className="btn btn--primary"
                disabled={!ready || busy}
                onClick={() => onCommit(note.trim())}
              >
                {busy ? <Icon name="spinner" size={13} /> : <Icon name="commit" size={14} />}
                Commit changes
              </button>
            )}
            {others.length > 0 && (
              <span className="publish__review">
                <select
                  className="input"
                  aria-label="Ask somebody to review"
                  value={reviewer}
                  disabled={busy}
                  onChange={(e) => setReviewer(e.target.value)}
                >
                  <option value="">Ask somebody to review…</option>
                  {others.map((person) => (
                    <option key={person.userId} value={person.userId}>
                      {person.displayName}
                    </option>
                  ))}
                </select>
                <button
                  className="btn btn--outline"
                  disabled={!ready || busy || !reviewer}
                  onClick={() => onSendForReview(note.trim(), reviewer || null)}
                >
                  Send for review
                </button>
              </span>
            )}
          </div>
          <p className="drawer__hint drawer__hint--foot">
            Committing saves a checkpoint. It does not merge to {aimedAt ?? 'main'} or change the
            published site.
          </p>
        </footer>
      </div>
    </div>
  )
}
